import thunk from "redux-thunk";
import storage from "redux-persist/lib/storage";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { persistStore, persistReducer } from "redux-persist";
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { composeWithDevTools } from "redux-devtools-extension/developmentOnly";

import auth from "./reducers/user";
import test from "./reducers/test";
import report from "./reducers/report";
import response from "./reducers/response";
import comments from "./reducers/comments";
import templates from "./reducers/templates";
import dashboard from "./reducers/dashboard";
import browserTest from "./reducers/browserTest";
import testpreview from "./reducers/testpreview";
import subscriptions from "./reducers/subscriptions";
import projectAnalysis from "./reducers/projectanalysis";
import dashboardModerated from "./reducers/dashboard-moderated";
import clientContacts from "./reducers/client-contacts";

import { USER } from "./actions/types";
import { TaskLogicVerificationMiddleware } from "./middleware/taskLogicVerificationMiddleware";
import { usePreviousTaskUrlMiddleware } from "./middleware/usePreviousTaskUrlMiddleware";
import { ScreenerValidationMiddleware } from "./middleware/screenerValidationMiddleware";
import { TaskFlowsQuotaVerificationMiddleware } from "./middleware/taskFlowsQuotaVerificationMiddleware";
import { TaskFlowsTaskVerificationMiddleware } from "./middleware/taskFlowsTaskVerificationMiddleware";

// Toolkit Reducers
import toolkitTest from "./components/toolkit/reducers/test";
import toolkitStudy from "./components/toolkit/reducers/study";
import toolkitReport from "./components/toolkit/reducers/report";
import toolkitSurvey from "./components/toolkit/reducers/survey";
import toolkitNotification from "./components/toolkit/reducers/notification";

// Combined Toolkit State
const toolkitState = combineReducers({
  test: toolkitTest,
  study: toolkitStudy,
  report: toolkitReport,
  survey: toolkitSurvey,
  notification: toolkitNotification,
});

// The `study` slice is a transient, per-session flow (active step, current test,
// redirect / over-quota URLs). Persisting it caused a finished study's terminal
// state to rehydrate on the next visit and fire its redirect before re-verification.
// `report` is fetched fresh too. A nested persist config keeps the other toolkit
// slices persisted while excluding these. (Top-level nested blacklist paths like
// "toolkitState.report" are ignored by redux-persist, which is why this is needed.)
const toolkitPersistConfig = {
  key: "toolkit",
  storage,
  stateReconciler: autoMergeLevel2,
  blacklist: ["study", "report"],
};
const persistedToolkitState = persistReducer(toolkitPersistConfig, toolkitState);

const persistConfig = {
  storage,
  key: "tbt",
  stateReconciler: autoMergeLevel2,
  blacklist: ["screener", "testpreview", "test", "browserTest", "response", "toolkitState", "clientContacts"],
};
const appReducer = combineReducers({
  auth,
  test,
  comments,
  response,
  templates,
  dashboard,
  browserTest,
  testpreview,
  subscriptions,
  projectAnalysis,
  testReport: report,
  dashboardModerated,
  clientContacts,
  toolkitState: persistedToolkitState, // Combined Toolkit State (nested persist; study/report excluded)
});

// Reset State on Logout
const rootReducer = (state, action) => {
  if (action.type === USER.LOGOUT) state = undefined;

  // if (localStorage["persist:tbt"]) console.log(JSON.parse(localStorage["persist:tbt"]));

  // if (localStorage?.state) {
  //   const oldState = JSON.parse(localStorage.state);
  //   state =
  //     oldState && oldState?.toolkitState?.study
  //       ? { toolkitState: { ...oldState?.toolkitState, study: oldState?.toolkitState?.study } }
  //       : undefined;
  // } else {
  //   state = undefined;
  // }

  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);
const devTools = process.env.REACT_APP_UXA_ENV === "live" ? [] : [composeWithDevTools()];
const middleware = [
  TaskLogicVerificationMiddleware,
  usePreviousTaskUrlMiddleware,
  ScreenerValidationMiddleware,
  TaskFlowsQuotaVerificationMiddleware,
  TaskFlowsTaskVerificationMiddleware,
  thunk,
];
export const store = configureStore({ reducer: persistedReducer, devTools, middleware });

export const persistor = persistStore(store);
